import type { SorterResult } from "antd/lib/table/interface";
import { type FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import type { DealerChangeLogList } from "src/app/features/DealerChangeLog/types/DealerChangeLogList";
import { type ExtendedColumnsType, GenericTable, type ResourceTableProps } from "../../../../../components";
import {
    displayDateTimeValue,
    displayEnumValue,
} from "../../../../../components/Table/components/PropertyTable/transformations/cellTransformations";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH } from "../../../../../core/router/types/routerTypes";

export const DealerChangeLogsTable: FC<ResourceTableProps<DealerChangeLogList>> = ({
    onStateChanged,
    onRowSelectionChange,
    datasource,
    resource,
}) => {
    const { t } = useTranslation();
    const getColumnsConfig = useCallback(
        (sortInfo: SorterResult<DealerChangeLogList>): ExtendedColumnsType<DealerChangeLogList>[] => {
            return [
                {
                    title: t("genericTable.column.id"),
                    dataIndex: "id",
                    key: "id",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "id" ? sortInfo.order : null,
                },
                {
                    title: t("dealerChangeLog.column.siteName"),
                    dataIndex: "siteName",
                    key: "siteName",
                    sorter: () => 0,
                    sortField: "orgUnit.name",
                    sortOrder: sortInfo.columnKey === "siteName" ? sortInfo.order : null,
                },
                {
                    title: t("dealerChangeLog.column.siteId"),
                    dataIndex: "siteId",
                    key: "siteId",
                    sorter: () => 0,
                    sortField: "orgUnit.siteId",
                    sortOrder: sortInfo.columnKey === "siteId" ? sortInfo.order : null,
                },
                {
                    title: t("dealerChangeLog.column.parentUnit"),
                    dataIndex: "parentUnit",
                    key: "parentUnit",
                    sorter: () => 0,
                    sortField: "orgUnit.organizationUnit.id",
                    sortOrder: sortInfo.columnKey === "parentUnit" ? sortInfo.order : null,
                },
                {
                    title: t("dealerChangeLog.column.dealerName"),
                    dataIndex: "dealerName",
                    key: "dealerName",
                    sorter: () => 0,
                    sortField: "orgUnit.dealerName",
                    sortOrder: sortInfo.columnKey === "dealerName" ? sortInfo.order : null,
                },
                {
                    title: t("dealerChangeLog.column.status"),
                    dataIndex: "status",
                    key: "status",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "status" ? sortInfo.order : null,
                    render: (value) => displayEnumValue(value, "dealer-change-log-statuses"),
                },
                {
                    title: t("genericTable.column.changed"),
                    dataIndex: "changed",
                    key: "changed",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "changed" ? sortInfo.order : null,
                    render: (value) => displayDateTimeValue(value),
                },
            ];
        },
        [t],
    );

    const navigate = useNavigate();
    const onRowClick = (record: DealerChangeLogList) => navigate(`${BASE_PATH}/${ResourceName.DEALER_CHANGE_LOGS}/${record.id}`);

    return (
        (datasource && (
            <GenericTable<DealerChangeLogList>
                name="dealerChangeLogs_table"
                getColumnsConfig={getColumnsConfig}
                datasource={datasource}
                onSelectionCallback={onRowSelectionChange}
                onStateChanged={onStateChanged}
                onRow={(record: DealerChangeLogList) => {
                    return {
                        onClick: () => onRowClick(record),
                    };
                }}
                resource={resource}
            />
        )) ||
        null
    );
};
